<template>
  <Teleport to="body">
    <Transition name="ps-banner">
      <div v-if="banner" class="ps-banner" role="alert" aria-live="assertive">
        <v-icon class="ps-banner__icon" :icon="banner.icon" size="22" />
        <div class="ps-banner__body">
          <strong class="ps-banner__title">{{ banner.title }}</strong>
          <span v-if="banner.message" class="ps-banner__message">{{ banner.message }}</span>
          <div v-if="banner.action || banner.dismissLabel" class="ps-banner__actions">
            <button v-if="banner.dismissLabel" type="button" class="ps-banner__later" @click="dismissBanner">
              {{ banner.dismissLabel }}
            </button>
            <button v-if="banner.action" type="button" class="ps-banner__action" @click="runBannerAction">
              {{ banner.action.label }}
            </button>
          </div>
        </div>
        <button v-if="!banner.dismissLabel" type="button" class="ps-banner__close" aria-label="Dismiss" @click="dismissBanner">
          <v-icon icon="mdi-close" size="16" />
        </button>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'
import { bannerState, dismissBanner, runBannerAction } from '@/services/ui/banner'

const banner = computed(() => bannerState.current)
</script>

<style scoped>
.ps-banner {
  position: fixed;
  left: 50%;
  top: calc(env(safe-area-inset-top) + 8px);
  transform: translateX(-50%);
  z-index: var(--ps-z-toast);
  width: min(calc(100vw - 24px), 440px);
  display: flex;
  align-items: flex-start;
  gap: var(--ps-space-2);
  padding: 13px 12px 13px 14px;
  border-radius: var(--ps-radius);
  background: var(--ps-ink);
  border: 1px solid var(--ps-ink-border);
  box-shadow: 0 14px 36px rgba(0, 0, 0, 0.35);
  color: var(--ps-text-on-ink);
  font-size: 14px;
  line-height: 1.4;
}

.ps-banner__icon {
  flex-shrink: 0;
  margin-top: 1px;
  color: var(--ps-accent);
}

.ps-banner__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ps-banner__title {
  font-size: 15px;
  font-weight: 700;
}

.ps-banner__message {
  opacity: 0.85;
  overflow-wrap: anywhere;
}

.ps-banner__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--ps-space-2);
  margin-top: 8px;
}

.ps-banner__action,
.ps-banner__later {
  padding: 7px 14px;
  border-radius: var(--ps-radius-pill);
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}

.ps-banner__action {
  border: none;
  background: var(--ps-accent);
  color: #1a1a1a;
}

.ps-banner__later {
  border: 1px solid var(--ps-ink-border);
  background: transparent;
  color: inherit;
}

.ps-banner__close {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: inherit;
  opacity: 0.7;
  cursor: pointer;
}

.ps-banner-enter-active,
.ps-banner-leave-active {
  transition: transform 0.35s cubic-bezier(0.2, 0.9, 0.3, 1.15), opacity 0.25s ease;
}

.ps-banner-enter-from,
.ps-banner-leave-to {
  transform: translate(-50%, calc(-100% - env(safe-area-inset-top) - 16px));
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .ps-banner-enter-active,
  .ps-banner-leave-active {
    transition: opacity 0.2s ease;
  }
  .ps-banner-enter-from,
  .ps-banner-leave-to {
    transform: translateX(-50%);
  }
}
</style>
